/**
 * API client for SILISENSE backend.
 * Base URL: VITE_API_BASE_URL (Azure API). Must be set at build time; backend runs on Azure.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

import { getStoredEmailToken } from '@/auth/emailAuth';

/** Exported for use in upload/fetch where custom headers are needed (e.g. FormData) */
export async function getAccessToken(): Promise<string | null> {
  // Email/password JWT takes precedence when present
  const emailToken = getStoredEmailToken();
  if (emailToken) return emailToken;
  try {
    const { msalInstance } = await import('@/auth/config');
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;
    const { apiRequest } = await import('@/auth/config');
    const res = await msalInstance.acquireTokenSilent({ ...apiRequest, account: accounts[0] });
    return res?.accessToken ?? null;
  } catch {
    return null;
  }
}

async function getToken(): Promise<string | null> {
  return getAccessToken();
}

function buildUrl(path: string): string {
  return path.startsWith('http') ? path : `${API_BASE.replace(/\/$/, '')}${path.startsWith('/') ? '' : '/'}${path}`;
}

export async function api<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = buildUrl(path);
  const token = await getToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    let msg = text || `HTTP ${res.status}`;
    let code: string | undefined;
    if (/Cannot (GET|POST|PUT|PATCH|DELETE) \/v1\//.test(text)) {
      msg =
        'API route not found on the backend. Restart or redeploy the backend with the latest API routes, then rebuild/redeploy the webapp if VITE_API_BASE_URL changed.';
    } else {
      try {
        const data = JSON.parse(text) as { error?: string; code?: string };
        if (typeof data?.error === 'string') msg = data.error;
        if (typeof data?.code === 'string') code = data.code;
      } catch {
        /* use text as-is */
      }
    }
    const err = new Error(msg) as Error & { code?: string };
    if (code) err.code = code;
    throw err;
  }
  const contentType = res.headers.get('content-type');
  if (contentType?.includes('application/json')) return res.json() as Promise<T>;
  return res.text() as Promise<T>;
}

/** Auth endpoints (login/register): no Bearer token so backend gets email/password only. */
export async function apiPostNoAuth<T>(path: string, body: unknown): Promise<T> {
  const url = buildUrl(path);
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    let msg = text || `HTTP ${res.status}`;
    let code: string | undefined;
    try {
      const data = JSON.parse(text) as { error?: string; code?: string };
      if (typeof data?.error === 'string') msg = data.error;
      if (typeof data?.code === 'string') code = data.code;
    } catch {
      /* use text as-is */
    }
    const err = new Error(msg) as Error & { code?: string };
    if (code) err.code = code;
    throw err;
  }
  const contentType = res.headers.get('content-type');
  if (contentType?.includes('application/json')) return res.json() as Promise<T>;
  return res.text() as Promise<T>;
}

export const apiGet = <T>(path: string) => api<T>(path, { method: 'GET' });
export const apiPost = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body) });
export const apiPut = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) });
export const apiPatch = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });

/** GET binary response (e.g. PDF report download). */
export async function apiDownloadBlob(path: string): Promise<Blob> {
  const url = buildUrl(path);
  const token = await getToken();
  const headers: HeadersInit = {};
  if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { method: 'GET', headers });
  if (!res.ok) {
    const text = await res.text();
    let msg = text || `HTTP ${res.status}`;
    try {
      const data = JSON.parse(text) as { error?: string };
      if (typeof data?.error === 'string') msg = data.error;
    } catch {
      /* use text */
    }
    throw new Error(msg);
  }
  return res.blob();
}
